import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Pill, Plus, User, Edit, Loader2, Save } from 'lucide-react';
import { API_URL, ROUTES, FREQUENCIES } from './constants';

export const PrescriptionsTab = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [medications, setMedications] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [patientFilter, setPatientFilter] = useState('all');
  const [specialistFilter, setSpecialistFilter] = useState('all');
  const [showNewForm, setShowNewForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [newPrescription, setNewPrescription] = useState({
    patient_id: '',
    medication_id: '',
    prescriber_id: '',
    dose: '',
    route: 'oral',
    frequency: 'daily',
    start_date: new Date().toISOString().split('T')[0],
    duration_days: 7,
    instructions: ''
  });

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchPrescriptions();
  }, [statusFilter, patientFilter, specialistFilter]);

  const fetchFilters = async () => {
    try {
      const [patientsRes, specialistsRes, medsRes] = await Promise.all([
        axios.get(`${API_URL}/api/patients?limit=100`),
        axios.get(`${API_URL}/api/specialists?limit=100`),
        axios.get(`${API_URL}/api/medications/catalog`)
      ]);
      setPatients(patientsRes.data);
      setSpecialists(specialistsRes.data);
      setMedications(medsRes.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const fetchPrescriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/medications/prescriptions`, {
        params: { 
          status: statusFilter !== 'all' ? statusFilter : undefined,
          patient_id: patientFilter !== 'all' ? patientFilter : undefined,
          prescriber_id: specialistFilter !== 'all' ? specialistFilter : undefined
        }
      });
      setPrescriptions(response.data);
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrescription = async () => {
    try {
      await axios.post(`${API_URL}/api/medications/prescriptions`, newPrescription);
      toast.success('Prescripción creada');
      setShowNewForm(false);
      setNewPrescription({
        patient_id: '',
        medication_id: '',
        prescriber_id: '',
        dose: '',
        route: 'oral',
        frequency: 'daily',
        start_date: new Date().toISOString().split('T')[0],
        duration_days: 7,
        instructions: ''
      });
      fetchPrescriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando prescripción');
    }
  };

  const handleEditPrescription = (pres) => {
    setEditingPrescription({
      id: pres.id,
      dose: pres.dose || '',
      route: pres.route || 'oral',
      frequency: pres.frequency || 'daily',
      instructions: pres.instructions || '',
      status: pres.status || 'active',
      medication_name: pres.medication_name,
      patient_name: pres.patient_name
    });
  };

  const handleUpdatePrescription = async () => {
    if (!editingPrescription) return;
    setEditLoading(true);
    try {
      await axios.put(`${API_URL}/api/medications/prescriptions/${editingPrescription.id}`, null, {
        params: {
          dose: editingPrescription.dose,
          route: editingPrescription.route,
          frequency: editingPrescription.frequency,
          instructions: editingPrescription.instructions,
          status: editingPrescription.status
        }
      });
      toast.success('Prescripción actualizada');
      setEditingPrescription(null);
      fetchPrescriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando prescripción');
    }
    setEditLoading(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={patientFilter} onValueChange={setPatientFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar paciente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los pacientes</SelectItem>
              {patients.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.first_name} {p.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={specialistFilter} onValueChange={setSpecialistFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar especialista" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los especialistas</SelectItem>
              {specialists.map(s => (
                <SelectItem key={s.id} value={s.id}>
                  {s.first_name} {s.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="active">Activas</SelectItem>
              <SelectItem value="completed">Completadas</SelectItem>
              <SelectItem value="suspended">Suspendidas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowNewForm(true)} data-testid="new-prescription-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Prescripción
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
        </div>
      ) : prescriptions.length > 0 ? (
        <div className="space-y-3">
          {prescriptions.map(pres => (
            <Card key={pres.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-[#FDE8EA] flex items-center justify-center">
                      <Pill className="h-6 w-6 text-[#E63946]" />
                    </div>
                    <div>
                      <p className="font-medium">{pres.medication_name}</p>
                      <p className="text-sm text-gray-600">
                        {pres.dose} - {ROUTES[pres.route]} - {FREQUENCIES[pres.frequency]}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        Inicio: {new Date(pres.start_date).toLocaleDateString('es-CO')}
                        {pres.end_date && ` | Fin: ${new Date(pres.end_date).toLocaleDateString('es-CO')}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      pres.status === 'active' ? 'bg-green-100 text-green-800' :
                      pres.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      {pres.status === 'active' ? 'Activa' :
                       pres.status === 'completed' ? 'Completada' :
                       pres.status === 'suspended' ? 'Suspendida' : pres.status}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEditPrescription(pres)}
                      className="h-8 w-8 p-0"
                      data-testid={`edit-prescription-${pres.id}`}
                    >
                      <Edit className="h-4 w-4 text-gray-500 hover:text-[#E63946]" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t text-sm text-gray-500 flex items-center justify-between">
                  <span>
                    <User className="h-3 w-3 inline mr-1" />
                    Paciente: {pres.patient_name || 'Sin asignar'}
                  </span>
                  <span className="text-blue-600">
                    Prescrito por: Dr. {pres.prescriber_name || 'Sin asignar'}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Pill className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No hay prescripciones {statusFilter === 'active' ? 'activas' : ''}</p>
        </div>
      )}

      {/* New Prescription Dialog */}
      <Dialog open={showNewForm} onOpenChange={setShowNewForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva Prescripción</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Paciente *</Label>
              <Select 
                value={newPrescription.patient_id}
                onValueChange={(v) => setNewPrescription({...newPrescription, patient_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar paciente" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Medicamento *</Label>
              <Select 
                value={newPrescription.medication_id}
                onValueChange={(v) => setNewPrescription({...newPrescription, medication_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar medicamento" />
                </SelectTrigger>
                <SelectContent>
                  {medications.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.generic_name} - {m.concentration}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Especialista Prescriptor *</Label>
              <Select 
                value={newPrescription.prescriber_id}
                onValueChange={(v) => setNewPrescription({...newPrescription, prescriber_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar especialista" />
                </SelectTrigger>
                <SelectContent>
                  {specialists.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      Dr. {s.first_name} {s.last_name} - {s.specialist_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dosis *</Label>
                <Input 
                  value={newPrescription.dose}
                  onChange={(e) => setNewPrescription({...newPrescription, dose: e.target.value})}
                  placeholder="Ej: 1 tableta"
                />
              </div>
              <div>
                <Label>Vía *</Label>
                <Select 
                  value={newPrescription.route}
                  onValueChange={(v) => setNewPrescription({...newPrescription, route: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROUTES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frecuencia *</Label>
                <Select 
                  value={newPrescription.frequency}
                  onValueChange={(v) => setNewPrescription({...newPrescription, frequency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FREQUENCIES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duración (días)</Label>
                <Input 
                  type="number"
                  value={newPrescription.duration_days}
                  onChange={(e) => setNewPrescription({...newPrescription, duration_days: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div>
              <Label>Fecha de Inicio</Label>
              <Input 
                type="date"
                value={newPrescription.start_date}
                onChange={(e) => setNewPrescription({...newPrescription, start_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Instrucciones</Label>
              <Input 
                value={newPrescription.instructions}
                onChange={(e) => setNewPrescription({...newPrescription, instructions: e.target.value})}
                placeholder="Ej: Tomar con alimentos"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewForm(false)}>Cancelar</Button>
            <Button onClick={handleCreatePrescription} data-testid="save-prescription-btn">
              Crear Prescripción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Prescription Dialog */}
      <Dialog open={!!editingPrescription} onOpenChange={() => setEditingPrescription(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Prescripción</DialogTitle>
            {editingPrescription && (
              <p className="text-sm text-gray-500">
                {editingPrescription.medication_name} - {editingPrescription.patient_name}
              </p>
            )}
          </DialogHeader>
          {editingPrescription && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Dosis</Label>
                  <Input 
                    value={editingPrescription.dose}
                    onChange={(e) => setEditingPrescription({...editingPrescription, dose: e.target.value})}
                    placeholder="Ej: 1 tableta"
                    data-testid="edit-prescription-dose"
                  />
                </div>
                <div>
                  <Label>Vía</Label>
                  <Select 
                    value={editingPrescription.route}
                    onValueChange={(v) => setEditingPrescription({...editingPrescription, route: v})}
                  >
                    <SelectTrigger data-testid="edit-prescription-route">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROUTES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Frecuencia</Label>
                  <Select 
                    value={editingPrescription.frequency}
                    onValueChange={(v) => setEditingPrescription({...editingPrescription, frequency: v})}
                  >
                    <SelectTrigger data-testid="edit-prescription-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(FREQUENCIES).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select 
                    value={editingPrescription.status}
                    onValueChange={(v) => setEditingPrescription({...editingPrescription, status: v})}
                  >
                    <SelectTrigger data-testid="edit-prescription-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activa</SelectItem>
                      <SelectItem value="completed">Completada</SelectItem>
                      <SelectItem value="suspended">Suspendida</SelectItem>
                      <SelectItem value="cancelled">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Instrucciones</Label>
                <Input 
                  value={editingPrescription.instructions}
                  onChange={(e) => setEditingPrescription({...editingPrescription, instructions: e.target.value})}
                  placeholder="Ej: Tomar con alimentos"
                  data-testid="edit-prescription-instructions"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPrescription(null)} disabled={editLoading}>Cancelar</Button>
            <Button onClick={handleUpdatePrescription} disabled={editLoading} data-testid="update-prescription-btn">
              {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Actualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrescriptionsTab;
